import type { MarkupPoint, MarkupPolygon } from './types'

export const POLYGON_CLOSE_DISTANCE = 0.025

export const isPointInPage = ({ x, y }: MarkupPoint) =>
  Number.isFinite(x) &&
  Number.isFinite(y) &&
  x >= 0 &&
  x <= 1 &&
  y >= 0 &&
  y <= 1

const crossProduct = (
  start: MarkupPoint,
  end: MarkupPoint,
  point: MarkupPoint
) =>
  (end.x - start.x) * (point.y - start.y) -
  (end.y - start.y) * (point.x - start.x)

const liesOnSegment = (
  point: MarkupPoint,
  start: MarkupPoint,
  end: MarkupPoint
) =>
  Math.min(start.x, end.x) <= point.x &&
  point.x <= Math.max(start.x, end.x) &&
  Math.min(start.y, end.y) <= point.y &&
  point.y <= Math.max(start.y, end.y)

const segmentsIntersect = (
  firstStart: MarkupPoint,
  firstEnd: MarkupPoint,
  secondStart: MarkupPoint,
  secondEnd: MarkupPoint
) => {
  const firstStartOrientation = crossProduct(firstStart, firstEnd, secondStart)
  const firstEndOrientation = crossProduct(firstStart, firstEnd, secondEnd)
  const secondStartOrientation = crossProduct(
    secondStart,
    secondEnd,
    firstStart
  )
  const secondEndOrientation = crossProduct(secondStart, secondEnd, firstEnd)
  if (
    firstStartOrientation * firstEndOrientation < 0 &&
    secondStartOrientation * secondEndOrientation < 0
  )
    return true
  return (
    (firstStartOrientation === 0 &&
      liesOnSegment(secondStart, firstStart, firstEnd)) ||
    (firstEndOrientation === 0 &&
      liesOnSegment(secondEnd, firstStart, firstEnd)) ||
    (secondStartOrientation === 0 &&
      liesOnSegment(firstStart, secondStart, secondEnd)) ||
    (secondEndOrientation === 0 &&
      liesOnSegment(firstEnd, secondStart, secondEnd))
  )
}

const isValidPolygon = (vertices: ReadonlyArray<MarkupPoint>) => {
  if (vertices.length < 3 || vertices.some((vertex) => !isPointInPage(vertex)))
    return false
  if (new Set(vertices.map(({ x, y }) => `${x},${y}`)).size !== vertices.length)
    return false
  const area = vertices.reduce((sum, vertex, index) => {
    const next = vertices[(index + 1) % vertices.length]!
    return sum + vertex.x * next.y - next.x * vertex.y
  }, 0)
  if (area === 0) return false
  for (let first = 0; first < vertices.length; first += 1) {
    const firstEnd = (first + 1) % vertices.length
    for (let second = first + 1; second < vertices.length; second += 1) {
      const secondEnd = (second + 1) % vertices.length
      if (firstEnd === second || secondEnd === first) continue
      if (
        segmentsIntersect(
          vertices[first]!,
          vertices[firstEnd]!,
          vertices[second]!,
          vertices[secondEnd]!
        )
      )
        return false
    }
  }
  return true
}

export const polygonFromVertices = (
  vertices: ReadonlyArray<MarkupPoint>
): MarkupPolygon | null => {
  const copiedVertices = vertices.map((vertex) => ({ ...vertex }))
  return isValidPolygon(copiedVertices)
    ? { shape: 'polygon', vertices: copiedVertices }
    : null
}

export const polygonDraftAfterClick = (
  vertices: ReadonlyArray<MarkupPoint>,
  point: MarkupPoint
) => {
  if (
    vertices.length >= 3 &&
    Math.hypot(point.x - vertices[0]!.x, point.y - vertices[0]!.y) <=
      POLYGON_CLOSE_DISTANCE
  )
    return { kind: 'complete' } as const
  if (vertices.some((vertex) => vertex.x === point.x && vertex.y === point.y))
    return { kind: 'duplicate' } as const
  return {
    kind: 'append',
    vertices: [...vertices, point] as MarkupPoint[],
  } as const
}

export const polygonValidationError = (vertices: ReadonlyArray<MarkupPoint>) =>
  vertices.length < 3
    ? 'Add at least three distinct vertices before completing the polygon.'
    : 'Polygon geometry is invalid.'

export const replacePolygonVertex = (
  polygon: MarkupPolygon,
  index: number,
  point: MarkupPoint
) =>
  index >= 0 && index < polygon.vertices.length
    ? polygonFromVertices(
        polygon.vertices.map((vertex, vertexIndex) =>
          vertexIndex === index ? point : vertex
        )
      )
    : null

export const removePolygonVertex = (polygon: MarkupPolygon, index: number) =>
  polygon.vertices.length <= 3
    ? null
    : polygonFromVertices(
        polygon.vertices.filter((_, vertexIndex) => vertexIndex !== index)
      )

export const insertPolygonVertex = (
  polygon: MarkupPolygon,
  edgeStartIndex: number,
  point: MarkupPoint
) => {
  if (
    edgeStartIndex < 0 ||
    edgeStartIndex >= polygon.vertices.length ||
    !isPointInPage(point)
  )
    return null
  const vertices = [...polygon.vertices]
  vertices.splice(edgeStartIndex + 1, 0, point)
  return polygonFromVertices(vertices)
}
